import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, useLabel } from "./adminLang";
import { Field } from "./fieldkit";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const ICON_OPTIONS = [
  "Cable", "Radio", "Satellite", "Globe", "Network", "ShieldCheck", "HardHat", "Lock",
  "Home", "Building2", "Tower", "Landmark", "Building", "Store",
];

const CONFIGS = {
  services: {
    entity: "services",
    title: "Solutions",
    testid: "admin-solutions",
    fields: [
      { key: "title", label: "Titre", type: "text" },
      { key: "short", label: "Résumé court", type: "textarea" },
      { key: "outcome", label: "Résultat pour le client", type: "textarea" },
      { key: "overview", label: "Aperçu technique", type: "textarea" },
      { key: "capabilities", label: "Capacités (une par ligne)", type: "list" },
      { key: "useCases", label: "Cas d'usage (un par ligne)", type: "list" },
      { key: "segments", label: "Segments (un par ligne)", type: "list" },
      { key: "benefits", label: "Avantages (un par ligne)", type: "list" },
      { key: "faq", label: "FAQ (Question :: Réponse, une par ligne)", type: "faq" },
      { key: "ctaLabel", label: "Libellé du bouton (optionnel)", type: "text" },
    ],
  },
  sectors: {
    entity: "sectors",
    title: "Secteurs",
    testid: "admin-sectors",
    fields: [
      { key: "title", label: "Titre", type: "text" },
      { key: "short", label: "Résumé court", type: "textarea" },
      { key: "body", label: "Description", type: "textarea" },
      { key: "points", label: "Points clés (un par ligne)", type: "list" },
    ],
  },
};

const emptyLocale = (fields) => Object.fromEntries(fields.map((f) => [f.key, f.type === "list" || f.type === "faq" ? [] : ""]));

const Catalogue = ({ kind }) => {
  const cfg = CONFIGS[kind];
  const { canWrite } = useAdmin();
  const { tr } = useAdminLang();
  const L = useLabel();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, slug, icon, order, published, fr, en }
  const [locale, setLocale] = useState("fr");
  const [saving, setSaving] = useState(false);

  const reload = () => api.get(`/admin/${cfg.entity}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    setRows([]); setEditing(null); reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const openNew = () => {
    setLocale("fr");
    setEditing({ slug: "", icon: "Globe", order: rows.length, published: true, fr: emptyLocale(cfg.fields), en: emptyLocale(cfg.fields) });
  };
  const openEdit = (r) => {
    const d = r.data || {};
    setLocale("fr");
    setEditing({
      id: r.id, slug: r.slug || "", icon: d.icon || "Globe", order: r.order ?? 0, published: r.published ?? true,
      fr: { ...emptyLocale(cfg.fields), ...(d.fr || {}) }, en: { ...emptyLocale(cfg.fields), ...(d.en || {}) },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        slug: editing.slug.trim(), order: Number(editing.order) || 0, published: !!editing.published,
        data: { icon: editing.icon, fr: editing.fr, en: editing.en },
      };
      if (editing.id) await api.patch(`/admin/${cfg.entity}/${editing.id}`, body);
      else await api.post(`/admin/${cfg.entity}`, body);
      setEditing(null);
      reload();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(tr("Confirmer la suppression ?", "Confirm deletion?"))) return;
    await api.delete(`/admin/${cfg.entity}/${id}`);
    reload();
  };

  const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF6600]";
  const cur = editing ? editing[locale] : {};
  const setField = (k, v) => setEditing((prev) => ({ ...prev, [locale]: { ...prev[locale], [k]: v } }));

  return (
    <div data-testid={cfg.testid}>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-black text-white">{tr(cfg.title, kind === "sectors" ? "Sectors" : "Solutions")}</h1>
        {canWrite && (
          <button onClick={openNew} data-testid={`${cfg.testid}-create`}
            className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-5 py-2.5 transition-colors">
            <Plus size={14} /> {tr("Créer", "Create")}
          </button>
        )}
      </div>

      <div className="mt-6 bg-[#101214] border border-white/10 rounded-2xl divide-y divide-white/5">
        {rows.length === 0 && <p className="px-6 py-10 text-center text-white/35 text-sm">{tr("Aucune donnée.", "No data.")}</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`${cfg.testid}-row-${r.slug}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">{(r.data?.fr?.title) || r.slug}</p>
              <p className="text-xs text-white/40">
                {r.published ? <span className="text-[#FF9900] font-bold">{tr("Publié", "Published")}</span> : <span>{tr("Non publié", "Unpublished")}</span>}
                <span> · /{r.slug} · {tr("ordre", "order")} {r.order}</span>
              </p>
            </div>
            {canWrite && (
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(r)} data-testid={`${cfg.testid}-edit-${r.slug}`} className="p-2 text-white/50 hover:text-[#FF9900]" aria-label="Modifier"><Pencil size={16} /></button>
                <button onClick={() => remove(r.id)} data-testid={`${cfg.testid}-delete-${r.slug}`} className="p-2 text-white/50 hover:text-red-400" aria-label="Supprimer"><Trash2 size={16} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setEditing(null)} data-testid={`${cfg.testid}-editor`}>
          <div className="w-full max-w-xl bg-[#101214] border-l border-white/10 h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-white">{editing.id ? tr("Modifier", "Edit") : tr("Créer", "Create")}</h2>
              <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white p-1"><X size={20} /></button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-white/45 mb-1.5">{L("Slug (URL)")}</label>
                <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className={inputCls} data-testid={`${cfg.testid}-slug`} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-white/45 mb-1.5">{L("Icône")}</label>
                <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className={inputCls} data-testid={`${cfg.testid}-icon`}>
                  {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-white/45 mb-1.5">{L("Ordre")}</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className={inputCls} data-testid={`${cfg.testid}-order`} />
              </div>
              <label className="flex items-end gap-2.5 text-sm text-white/70 pb-2">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-4 h-4 accent-[#FF6600]" data-testid={`${cfg.testid}-published`} />
                {L("Publié")}
              </label>
            </div>

            <div className="mt-6 flex gap-2" data-testid={`${cfg.testid}-locale-tabs`}>
              {["fr", "en"].map((lc) => (
                <button key={lc} onClick={() => setLocale(lc)} data-testid={`${cfg.testid}-tab-${lc}`}
                  className={`text-xs font-black rounded-full px-4 py-1.5 transition-colors ${locale === lc ? "bg-[#FF6600] text-[#08090A]" : "bg-white/5 text-white/60 hover:text-white"}`}>
                  {lc.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="mt-5 space-y-4">
              {cfg.fields.map((f) => (
                <Field key={f.key} field={f} value={cur[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>

            <div className="mt-8 flex gap-3">
              <button onClick={save} disabled={saving} data-testid={`${cfg.testid}-save`}
                className="bg-[#FF6600] hover:bg-[#FF9900] disabled:opacity-60 text-[#08090A] text-xs font-black rounded-full px-6 py-3 transition-colors">
                {saving ? tr("Enregistrement…", "Saving…") : tr("Enregistrer", "Save")}
              </button>
              <button onClick={() => setEditing(null)} className="text-xs font-bold text-white/50 hover:text-white px-4">{tr("Annuler", "Cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogue;
